import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  getUrls,
  getVersionToLoad,
} from '@fec/frontend/elements/media/image-ng/image-ng.js';
import ResizeListener from '@fec/frontend/foundation/client/resize-listener.js';

const ImageNg = ({
  styleModifier = '',
  image = null,
  forcedStack = null,
  noBackground = false,
}) => {
  const { provider, id, alt } = image ?? {};
  const imageRef = useRef();
  const [stackToUse, setStackToUse] = useState(forcedStack);
  const [sources, setSources] = useState([]);

  const getStackToUse = () => {
    if (provider) {
      const height = imageRef.current.clientHeight;
      const width = imageRef.current.clientWidth;

      const newStackToUse = getVersionToLoad(
        provider,
        width,
        height,
        stackToUse,
      );
      if (newStackToUse) {
        setStackToUse(newStackToUse);
      }
    }
  };

  useEffect(() => {
    if (!imageRef.current) {
      return;
    }

    // unless a stack is enforced, we need to listen to browser resizes
    let subscriptionId;
    if (!forcedStack) {
      getStackToUse();
      subscriptionId = ResizeListener.subscribeDebounced(() => {
        getStackToUse();
      });
    }

    return () => {
      if (!forcedStack) {
        ResizeListener.unsubscribe(subscriptionId);
      }
    };
  }, [imageRef.current, forcedStack]);

  // if the stack to use changes, we need to update the sources
  useEffect(() => {
    if (stackToUse && id) {
      setSources(Object.entries(getUrls(provider, id, stackToUse)));
    }
  }, [stackToUse, id, provider]);

  return (
    <div
      className={classNames('image-ng', styleModifier, {
        'image-ng--no-bg': noBackground,
      })}
      ref={imageRef}>
      <picture>
        {sources.map(([type, srcSet]) => (
          <source key={type} type={type} srcSet={srcSet} />
        ))}
        <img
          alt={alt}
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          className="image-ng__img"
          loading="lazy"
        />
      </picture>
    </div>
  );
};

ImageNg.propTypes = {
  styleModifier: PropTypes.string,
  image: PropTypes.object,
  forcedStack: PropTypes.string,
  noBackground: PropTypes.bool,
};

export default ImageNg;
